import { useEffect } from 'react';

import { ArrowClockwise } from '@phosphor-icons/react';
import { Typography } from '@remarkable/ark-web';
import * as Sentry from '@sentry/react';
import { ComponentRendered } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { IS_DEVELOPMENT } from 'src/config';
import { TestId } from 'src/testids';
import { capitalize } from 'src/utils/capitalize';
import {
  AccessDeniedError,
  AccountLinkingMismatchError,
  EmailNotVerifiedError,
  DomainNotForwardedError,
  DomainNotVerifiedInOrgError,
  LoginValidatorError,
  MultipleCandidatesError,
  OrganizationNotFoundError,
  SAMLEmailNotFoundError,
  SocialAccountNotFoundError,
  SocialLoginConflictError,
  SSOLoginRequiredError,
} from 'src/utils/errors';

import { Select, Trans } from '@lingui/react/macro';
import { HTTPError } from 'src/api/createApiClient.types';
import { Button } from './Button';
import { ErrorAccountLinkingMismatch } from './ErrorAccountLinkingMismatch';
import { Link2 } from './Link2';
import { NotificationBox } from './NotificationBox';
import { SentryFeedbackReportButton } from './SentryFeedbackReportButton';
import { VerifyEmail } from './VerifyEmail';

interface AuthErrorProps {
  title: React.ReactNode;
  children: React.ReactNode;
}

const AuthError = ({ title, children }: AuthErrorProps) => (
  <div data-cy="auth-error" className="w-full bg-neutral-light-2">
    <div className="m-auto flex w-full max-w-screen-md flex-col items-center gap-24 px-24 py-64 md:px-64 lg:py-128">
      <Typography variant="heading-lg" className="text-center">
        {title}
      </Typography>
      <div className="max-w-[560px] text-center">{children}</div>
      <Link2
        to="/logout"
        as="button"
        variant="primary"
        className="ark-button ark-button--primary"
        componentLocation="something went wrong - auth error"
      >
        <Trans>Sign back in</Trans>
      </Link2>
    </div>
  </div>
);

interface Props {
  error: Error | null;
  sentryEventId?: string;
  reset?: () => void;
  info?: unknown;
}

type ErrorRenderer = (error: Error | null) => React.ReactNode;

const forError =
  <T extends Error>(
    errorType: new (...args: never[]) => T,
    render: (error: T) => React.ReactNode
  ): ErrorRenderer =>
  (error) =>
    error instanceof errorType ? render(error) : null;

const getSocialLoginProvider = (message: string) =>
  ['google', 'apple', 'facebook'].includes(message)
    ? capitalize(message)
    : null;

const authErrorRenderers: ErrorRenderer[] = [
  forError(EmailNotVerifiedError, () => <VerifyEmail />),
  forError(SocialLoginConflictError, (error) => {
    const ssoProvider = getSocialLoginProvider(error.message);
    return (
      <AuthError title={<Trans>Account already connected</Trans>}>
        <p>
          <Trans>
            It looks like this account is already linked to a different social
            login. You can only connect one social account (like Google,
            Facebook, or Apple) at a time.
          </Trans>
          <br />
          <br />
          <Select
            value={ssoProvider ? 'hasSingleSignOn' : 'no-sso'}
            _hasSingleSignOn={
              <Trans>To continue, try logging in with {ssoProvider}.</Trans>
            }
            other="To continue, try logging in with a different account."
          />
        </p>
      </AuthError>
    );
  }),
  forError(LoginValidatorError, () => (
    <AuthError title={<Trans>Login validation error</Trans>}>
      <p>
        <Trans>
          There was an error validating your login. Please try again or contact
          support if the issue persists.
        </Trans>
      </p>
    </AuthError>
  )),
  forError(SAMLEmailNotFoundError, () => (
    <AuthError title={<Trans>Email not found</Trans>}>
      <p data-cy="saml-email-not-found-error">
        <Trans>
          We couldn&apos;t find the <code>email</code> attribute in the SAML
          response from your identity provider.
          <br />
          Please contact your administrator to ensure your email is provided
          through your SSO integration.
        </Trans>
      </p>
    </AuthError>
  )),
  forError(OrganizationNotFoundError, () => (
    <AuthError title={<Trans>Organization not found</Trans>}>
      <p>
        <Trans>
          We couldn&apos;t find the organization associated with your SSO
          account. Please contact your administrator to ensure your organization
          is set up correctly.
        </Trans>
      </p>
    </AuthError>
  )),
  forError(MultipleCandidatesError, () => (
    <AuthError title={<Trans>Multiple existing accounts found</Trans>}>
      <p>
        <Trans>
          We found multiple existing accounts associated with your email. Please
          contact support to resolve this issue.
        </Trans>
      </p>
    </AuthError>
  )),
  forError(DomainNotForwardedError, () => (
    <AuthError title={<Trans>Domain not forwarded</Trans>}>
      <p>
        <Trans>
          The domain for your email address is not forwarded to SSO integration.
          Please contact your administrator to ensure your domain is forwarded
          correctly.
          <br />
          If your organization is testing this feature, please ensure that you
          have an existing reMarkable account before proceeding.
        </Trans>
      </p>
    </AuthError>
  )),
  forError(DomainNotVerifiedInOrgError, () => (
    <AuthError title={<Trans>Domain not verified</Trans>}>
      <p>
        <Trans>
          The domain for your email address is not verified in the organization.
          Please contact your administrator to ensure your domain is verified
          correctly.
        </Trans>
      </p>
    </AuthError>
  )),
  forError(SocialAccountNotFoundError, (error) => {
    const ssoProvider = getSocialLoginProvider(error.message);

    return (
      <AuthError title={<Trans>Couldn&apos;t find that account</Trans>}>
        <p>
          <Trans>
            We couldn&apos;t find an account connected to that {ssoProvider}{' '}
            login.
            <br />
            Try logging in with your email or create a new account.
          </Trans>
        </p>
      </AuthError>
    );
  }),
  forError(SSOLoginRequiredError, () => (
    <AuthError title={<Trans>Log in with your work email</Trans>}>
      <p>
        <Trans>
          Your account is part of an organization that uses single sign-on
          (SSO). Social logins (Google, Facebook, or Apple) aren&apos;t
          supported for SSO-managed accounts.
          <br />
          To access your account, log in with your work email instead of a
          social login.
        </Trans>
      </p>
    </AuthError>
  )),
  forError(AccessDeniedError, (error) => (
    <div data-cy="error-page" className="w-full bg-neutral-light-2">
      <div
        data-cy={TestId.PageAccessDenied}
        className="m-auto flex w-full max-w-screen-md flex-col items-center gap-24 px-24 py-64 md:px-64 lg:py-128"
      >
        <Typography variant="heading-lg" className="text-center">
          <Trans>Access Denied</Trans>
        </Typography>
        <p className="max-w-[560px] text-center">
          {error.message || (
            <Trans>
              You do not have permission to access this page. Please contact
              your administrator if you believe this is an error.
            </Trans>
          )}
        </p>
        <div className="flex gap-16">
          <Link2
            to="/logout"
            as="button"
            variant="secondary"
            className="ark-button ark-button--secondary"
            componentLocation="something went wrong - access denied"
          >
            <Trans>Logout</Trans>
          </Link2>
          <Link2
            to="/"
            as="button"
            variant="primary"
            className="ark-button ark-button--primary"
            componentLocation="something went wrong - access denied"
          >
            <Trans context="Link that points to the main page">
              Go to home
            </Trans>
          </Link2>
        </div>
      </div>
    </div>
  )),
  forError(AccountLinkingMismatchError, (error) => (
    <ErrorAccountLinkingMismatch error={error} />
  )),
];

export const SomethingWentWrong = ({ error, sentryEventId, reset }: Props) => {
  useEffect(() => {
    tracker.trackEvent(
      new ComponentRendered({
        component_name: 'SomethingWentWrong',
        component_type: 'error',
      })
    );
  }, []);

  const knownError = authErrorRenderers
    .map((render) => render(error))
    .find((result) => result !== null);

  if (knownError) return knownError;

  return (
    <UnknownError error={error} sentryEventId={sentryEventId} reset={reset} />
  );
};

const UnknownError = ({ error, sentryEventId, reset }: Props) => {
  useEffect(() => {
    Sentry.withScope((scope) => {
      const isHttpError = error instanceof HTTPError;

      scope.setTags({
        'rm.is_http_error': isHttpError,
        'rm.something_went_wrong_page': true,
      });

      if (isHttpError) {
        scope.setTags({
          'rm.http_error_url': error.request.url,
          'rm.http_error_status': error.response.statusText,
        });
      }

      scope.captureException(error);
    });
  }, []);

  return (
    <div data-cy="error-page" className="w-full bg-neutral-light-2">
      <div className="m-auto flex w-full max-w-screen-md flex-col items-center gap-24 px-24 py-64 md:px-64 lg:py-128">
        <Typography variant="heading-lg" className="text-center">
          <Trans>Sorry, something went wrong</Trans>
        </Typography>
        <p className="max-w-[560px] text-center">
          <Trans>
            This may be an issue with cookies, too many open login or a system
            outage. We&apos;re alerted about this type of issue automatically.
            Please try again later.
          </Trans>
        </p>

        {IS_DEVELOPMENT && error ? (
          <NotificationBox
            error={error}
            title={error.name}
            useErrorMessage
            className="w-full"
          />
        ) : null}

        <div className="flex items-center gap-16">
          <Button
            onClick={() => {
              if (reset) {
                reset();
              } else {
                window.location.reload();
              }
            }}
          >
            <ArrowClockwise />
            <span>
              <Trans context="Reloading the webpage">Refresh</Trans>
            </span>
          </Button>
          <SentryFeedbackReportButton eventId={sentryEventId} />
        </div>
      </div>
    </div>
  );
};
